import { ART_PALETTE } from './art-palette.js';
import { DAY_NIGHT } from './day-night-config.js';
import { duskPalette, nightPalette, parseRgba, parseTint } from './day-night.js';

const VERTEX_SOURCE = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = vec2(aPos.x * 0.5 + 0.5, aPos.y * 0.5 + 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAGMENT_SOURCE = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uScene;
uniform sampler2D uMask;
uniform sampler2D uDayPalette;
uniform sampler2D uNightPalette;
uniform sampler2D uDuskPalette;
uniform float uNightStrength;
uniform vec4 uNightParams;
uniform vec4 uNightTint;
uniform vec4 uDuskParams;
uniform vec4 uDuskTint;

vec3 rgb2hsl(vec3 c) {
  float maxc = max(max(c.r, c.g), c.b);
  float minc = min(min(c.r, c.g), c.b);
  float l = (maxc + minc) * 0.5;
  if (maxc == minc) return vec3(0.0, 0.0, l);
  float d = maxc - minc;
  float s = l > 0.5 ? d / (2.0 - maxc - minc) : d / (maxc + minc);
  float h = 0.0;
  if (maxc == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
  else if (maxc == c.g) h = (c.b - c.r) / d + 2.0;
  else h = (c.r - c.g) / d + 4.0;
  return vec3(h / 6.0, s, l);
}

float hueToRgb(float p, float q, float t) {
  float h = t;
  if (h < 0.0) h += 1.0;
  if (h > 1.0) h -= 1.0;
  if (h < 1.0 / 6.0) return p + (q - p) * 6.0 * h;
  if (h < 0.5) return q;
  if (h < 2.0 / 3.0) return p + (q - p) * (2.0 / 3.0 - h) * 6.0;
  return p;
}

vec3 hsl2rgb(vec3 hsl) {
  if (hsl.y == 0.0) return vec3(hsl.z);
  float q = hsl.z < 0.5 ? hsl.z * (1.0 + hsl.y) : hsl.z + hsl.y - hsl.z * hsl.y;
  float p = 2.0 * hsl.z - q;
  return vec3(
    hueToRgb(p, q, hsl.x + 1.0 / 3.0),
    hueToRgb(p, q, hsl.x),
    hueToRgb(p, q, hsl.x - 1.0 / 3.0)
  );
}

vec3 applyGrade(vec3 color, vec4 params, vec4 tint) {
  float contrast = params.w;
  vec3 contrasted = clamp((color - vec3(0.5)) * contrast + vec3(0.5), 0.0, 1.0);
  vec3 hsl = rgb2hsl(contrasted);
  hsl.x = fract(hsl.x + params.x / 360.0);
  hsl.y = clamp(hsl.y * params.y, 0.0, 1.0);
  hsl.z = clamp(hsl.z * params.z, 0.0, 1.0);
  return mix(hsl2rgb(hsl), tint.rgb, tint.a);
}

void main() {
  vec4 scene = texture2D(uScene, vUv);
  // red = light reaching this pixel, green = how dark it sits before any light.
  vec2 mask = texture2D(uMask, vUv).rg;
  vec3 dayColor = scene.rgb;
  vec3 duskColor = applyGrade(dayColor, uDuskParams, uDuskTint);
  vec3 nightColor = applyGrade(dayColor, uNightParams, uNightTint);
  for (int i = 0; i < 32; i++) {
    float u = (float(i) + 0.5) / 32.0;
    vec4 pal = texture2D(uDayPalette, vec2(u, 0.5));
    // Transparent entries share RGB with real colors (index 0 is #00000000,
    // index 31 is opaque black); matching one would shadow the real slot.
    if (pal.a < 0.5) continue;
    vec3 delta = abs(dayColor - pal.rgb);
    if (delta.r + delta.g + delta.b < 0.008) {
      duskColor = texture2D(uDuskPalette, vec2(u, 0.5)).rgb;
      nightColor = texture2D(uNightPalette, vec2(u, 0.5)).rgb;
      break;
    }
  }
  // Day -> warm dusk over the first half of the transition, dusk -> cool
  // night over the second half; orange fading into blue passes through purple.
  // The green channel is a floor on the hour: an interior grades to night even
  // at noon, so a windowless room is dark whatever the clock says. Outdoors it
  // is zero and this is exactly uNightStrength.
  float s = clamp(max(uNightStrength, mask.g), 0.0, 1.0);
  vec3 shaded;
  if (s < 0.5) shaded = mix(dayColor, duskColor, s * 2.0);
  else shaded = mix(duskColor, nightColor, s * 2.0 - 1.0);
  vec3 lit = mix(shaded, dayColor, clamp(mask.r, 0.0, 1.0));
  gl_FragColor = vec4(lit, 1.0);
}
`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(log);
  }
  return shader;
}

function fallbackOverlayTint(nightStrength, config) {
  const dusk = parseTint(config.fallbackDuskTint ?? config.fallbackTint);
  const night = parseTint(config.fallbackTint);
  const t = nightStrength < 0.5 ? 0 : nightStrength * 2 - 1;
  const mixed = dusk.map((value, i) => Math.round((value + (night[i] - value) * t) * 255));
  return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
}

function paletteTextureData(palette) {
  const data = new Uint8Array(32 * 4);
  for (let i = 0; i < 32; i++) {
    const [r, g, b, a] = parseRgba(palette[i] ?? '#000000ff');
    data[i * 4] = r;
    data[i * 4 + 1] = g;
    data[i * 4 + 2] = b;
    data[i * 4 + 3] = a;
  }
  return data;
}

export class NightCompositor {
  constructor(config = DAY_NIGHT) {
    this.config = config;
    this.width = 0;
    this.height = 0;
    this.glCanvas = typeof document === 'undefined' ? null : document.createElement('canvas');
    this.fallbackCanvas = typeof document === 'undefined' ? null : document.createElement('canvas');
    this.overlayCanvas = typeof document === 'undefined' ? null : document.createElement('canvas');
    this.gl = null;
    this.program = null;
    this.fallbackCtx = null;
    this.overlayCtx = null;
    this.sceneTexture = null;
    this.maskTexture = null;
    if (this.glCanvas) this._initGl();
    if (this.fallbackCanvas) {
      this.fallbackCtx = this.fallbackCanvas.getContext('2d', { alpha: false });
      this.overlayCtx = this.overlayCanvas.getContext('2d', { alpha: true });
    }
  }

  _initGl() {
    // The composited canvas is drawImage'd to the main canvas in the same task
    // as drawArrays, so the buffer never needs preserving across frames.
    const options = { alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: false, antialias: false };
    const gl = this.glCanvas.getContext('webgl', options) || this.glCanvas.getContext('experimental-webgl', options);
    if (!gl) return;
    try {
      const program = gl.createProgram();
      const vertexShader = compile(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
      const fragmentShader = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SOURCE);
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program));
      }
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1, 1, -1, -1, 1, 1, 1,
      ]), gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(program, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      this.sceneTexture = gl.createTexture();
      this.maskTexture = gl.createTexture();
      this.dayPaletteTexture = gl.createTexture();
      this.nightPaletteTexture = gl.createTexture();
      this.duskPaletteTexture = gl.createTexture();
      for (const texture of [
        this.sceneTexture, this.maskTexture, this.dayPaletteTexture, this.nightPaletteTexture,
        this.duskPaletteTexture,
      ]) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      }

      this.gl = gl;
      this.program = program;
      this.locations = {
        scene: gl.getUniformLocation(program, 'uScene'),
        mask: gl.getUniformLocation(program, 'uMask'),
        dayPalette: gl.getUniformLocation(program, 'uDayPalette'),
        nightPalette: gl.getUniformLocation(program, 'uNightPalette'),
        duskPalette: gl.getUniformLocation(program, 'uDuskPalette'),
        nightStrength: gl.getUniformLocation(program, 'uNightStrength'),
        nightParams: gl.getUniformLocation(program, 'uNightParams'),
        nightTint: gl.getUniformLocation(program, 'uNightTint'),
        duskParams: gl.getUniformLocation(program, 'uDuskParams'),
        duskTint: gl.getUniformLocation(program, 'uDuskTint'),
      };
      // Texture-unit bindings never change; set them once.
      gl.useProgram(program);
      gl.uniform1i(this.locations.scene, 0);
      gl.uniform1i(this.locations.mask, 1);
      gl.uniform1i(this.locations.dayPalette, 2);
      gl.uniform1i(this.locations.nightPalette, 3);
      gl.uniform1i(this.locations.duskPalette, 4);
      this._paletteConfig = null;
      this._textureSizes = new Map();
    } catch (error) {
      console.warn('Night compositor falling back to Canvas 2D.', error);
      this.gl = null;
      this.program = null;
    }
  }

  resize(width, height) {
    this.width = width;
    this.height = height;
    for (const canvas of [this.glCanvas, this.fallbackCanvas, this.overlayCanvas]) {
      if (!canvas) continue;
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
    }
    if (this.gl) this.gl.viewport(0, 0, width, height);
    if (this.fallbackCtx) this.fallbackCtx.imageSmoothingEnabled = false;
    if (this.overlayCtx) this.overlayCtx.imageSmoothingEnabled = false;
  }

  /**
   * `stencilCanvas` is only consulted on the Canvas-2D path, which cannot read
   * an individual mask channel and needs the darkness pre-baked into an alpha.
   */
  composite(sceneCanvas, maskCanvas, nightStrength, config = this.config, stencilCanvas = null) {
    if (this.gl && this.program) return this._compositeGl(sceneCanvas, maskCanvas, nightStrength, config);
    return this._composite2d(sceneCanvas, stencilCanvas, nightStrength, config);
  }

  _uploadCanvas(gl, texture, unit, canvas) {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    // Re-specifying the texture reallocates driver storage every frame;
    // update in place unless the canvas size actually changed.
    const size = canvas.width * 0x10000 + canvas.height;
    if (this._textureSizes.get(texture) === size) {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      this._textureSizes.set(texture, size);
    }
  }

  _uploadPalette(gl, texture, unit, palette) {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 32, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, paletteTextureData(palette),
    );
  }

  _uploadGrade(gl, paramsLocation, tintLocation, grade) {
    // Same defaults as the CPU path (applyNightTransformRgb) — a grade that
    // omits a field must not feed NaN into the shader.
    gl.uniform4f(
      paramsLocation,
      grade.hueShiftDeg ?? 0, grade.saturation ?? 1, grade.brightness ?? 1, grade.contrast ?? 1,
    );
    const tint = grade.tint ? parseTint(grade.tint) : [0, 0, 0];
    gl.uniform4f(tintLocation, tint[0], tint[1], tint[2], grade.tintStrength ?? 0);
  }

  _compositeGl(sceneCanvas, maskCanvas, nightStrength, config) {
    const gl = this.gl;
    gl.useProgram(this.program);
    this._uploadCanvas(gl, this.sceneTexture, 0, sceneCanvas);
    this._uploadCanvas(gl, this.maskTexture, 1, maskCanvas);
    // The three palettes and both grades are pure functions of the config;
    // recomputing ~64 color transforms and re-uploading three textures per
    // frame was pure waste. Refresh only when the config object changes.
    if (this._paletteConfig !== config) {
      this._uploadPalette(gl, this.dayPaletteTexture, 2, ART_PALETTE);
      this._uploadPalette(gl, this.nightPaletteTexture, 3, nightPalette(ART_PALETTE, config));
      this._uploadPalette(gl, this.duskPaletteTexture, 4, duskPalette(ART_PALETTE, config));
      this._uploadGrade(gl, this.locations.nightParams, this.locations.nightTint, config.night);
      this._uploadGrade(gl, this.locations.duskParams, this.locations.duskTint, config.dusk);
      this._paletteConfig = config;
    }
    gl.uniform1f(this.locations.nightStrength, nightStrength);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.viewport(0, 0, this.width, this.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    return this.glCanvas;
  }

  _composite2d(sceneCanvas, stencilCanvas, nightStrength, config) {
    const ctx = this.fallbackCtx;
    const overlay = this.overlayCtx;
    const { width, height } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(sceneCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    if (!stencilCanvas) return this.fallbackCanvas;

    overlay.setTransform(1, 0, 0, 1, 0, 0);
    overlay.globalCompositeOperation = 'copy';
    overlay.globalAlpha = 1;
    overlay.clearRect(0, 0, width, height);
    overlay.globalCompositeOperation = 'source-over';
    overlay.fillStyle = fallbackOverlayTint(nightStrength, config);
    overlay.globalAlpha = config.fallbackMaxAlpha;
    overlay.fillRect(0, 0, width, height);
    overlay.globalAlpha = 1;
    // The stencil's alpha is the finished darkness, so keeping the tint only
    // where the stencil is opaque scales it by that darkness. Reading the light
    // mask here instead would be wrong: it is opaque everywhere by design.
    overlay.globalCompositeOperation = 'destination-in';
    overlay.drawImage(stencilCanvas, 0, 0);

    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(this.overlayCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    return this.fallbackCanvas;
  }
}
